import { useState, useEffect, useCallback } from 'react';
import { MessageSquare, Plus, Settings, BookOpen, Users, HelpCircle, Database, Trash2, Building2, Upload, Mail, Inbox, UserCheck, UserCircle, Lightbulb, Activity, Zap, Sparkles, Clock } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { VersionBadge } from './VersionBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Sidebar = ({ user, onCloseMobile, activeChatId, onSelectChat, onNewChat }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openQuestionsCount, setOpenQuestionsCount] = useState(0);
  const [mailInboxCount, setMailInboxCount] = useState(0);

  const isAdmin = user && user.role === 'admin';
  const isGuest = !user || user.role === 'guest';
  // UMitza-Check: nur dieser User sieht Mail-Modul
  const isUMitza = user && (user.username === 'UMitza' || user.username === 'admin');

  const getToken = () => {
    const savedUser = localStorage.getItem('gb-sylt-user');
    if (savedUser) {
      try {
        const parsed = JSON.parse(savedUser);
        return parsed.token;
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  const loadOpenQuestionsCount = useCallback(async () => {
    if (!isAdmin) {
      setOpenQuestionsCount(0);
      return;
    }

    const token = getToken();
    if (!token) return;

    try {
      const response = await axios.get(`${API}/open-questions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Count only open questions (not resolved)
      const openCount = (response.data.questions || []).filter(q => q.status === 'open').length;
      setOpenQuestionsCount(openCount);
    } catch (error) {
      console.error('Failed to load open questions count:', error);
      setOpenQuestionsCount(0);
    }
  }, [isAdmin]);

  const loadMailInboxCount = useCallback(async () => {
    if (!isUMitza) {
      setMailInboxCount(0);
      return;
    }

    const token = getToken();
    if (!token) return;

    try {
      const response = await axios.get(`${API}/mail/inbox`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMailInboxCount(response.data.total || 0);
    } catch (error) {
      // Fehler still ignorieren (API existiert evtl. noch nicht)
      setMailInboxCount(0);
    }
  }, [isUMitza]);

  const loadChatHistory = useCallback(async () => {
    if (isGuest) {
      setConversations([]);
      return;
    }

    const token = getToken();
    if (!token) return;

    setLoading(true);
    try {
      const response = await axios.get(`${API}/chat-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data.chats || []);
    } catch (error) {
      console.error('Failed to load chat history:', error);
      setConversations([]);
    } finally {
      setLoading(false);
    }
  }, [isGuest]);

  useEffect(() => {
    loadChatHistory();
    loadOpenQuestionsCount();
    loadMailInboxCount();
  }, [loadChatHistory, loadOpenQuestionsCount, loadMailInboxCount]);

  // Expose reload functions for parent components
  useEffect(() => {
    window.reloadChatHistory = loadChatHistory;
    window.reloadOpenQuestionsCount = loadOpenQuestionsCount;
    window.reloadMailInboxCount = loadMailInboxCount;
    return () => {
      delete window.reloadChatHistory;
      delete window.reloadOpenQuestionsCount;
      delete window.reloadMailInboxCount;
    };
  }, [loadChatHistory, loadOpenQuestionsCount, loadMailInboxCount]);

  const formatTimestamp = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (hours < 1) return 'Gerade eben';
    if (hours < 24) return `Vor ${hours}h`;
    if (days < 2) return 'Gestern';
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  };

  const handleNewChat = () => {
    if (onNewChat) {
      onNewChat();
    }
    navigate('/');
    if (onCloseMobile) onCloseMobile();
  };

  const handleSelectChat = (chat) => {
    if (onSelectChat) {
      onSelectChat(chat);
    }
    navigate('/');
    if (onCloseMobile) onCloseMobile();
  };

  const handleDeleteChat = async (e, chatId) => {
    e.stopPropagation();
    const token = getToken();
    if (!token) return;

    try {
      await axios.delete(`${API}/chat-history/${chatId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(prev => prev.filter(c => c.id !== chatId));
      if (activeChatId === chatId && onNewChat) {
        onNewChat();
      }
    } catch (error) {
      console.error('Failed to delete chat:', error);
    }
  };

  const isActive = (path) => location.pathname === path;

  const handleNavigation = (path) => {
    navigate(path);
    if (onCloseMobile) onCloseMobile();
  };

  // Navigation items for employees (non-admin logged-in users)
  const employeeNavItems = [
    { path: '/hausliste', icon: Building2, label: 'Hausliste', badge: 0 },
    { path: '/admin/oeffnungszeiten', icon: Clock, label: 'Oeffnungszeiten', badge: 0 },
    { path: '/profile', icon: UserCircle, label: 'Mein Profil', badge: 0 }
  ];

  // Admin navigation items (includes employee items plus admin-only items)
  const adminNavItems = [
    { path: '/knowledge', icon: BookOpen, label: 'Wissensverwaltung', badge: 0 },
    { path: '/hausliste', icon: Building2, label: 'Hausliste', badge: 0 },
    { path: '/admin/oeffnungszeiten', icon: Clock, label: 'Oeffnungszeiten', badge: 0 },
    { path: '/open-questions', icon: HelpCircle, label: 'Offene Fragen', badge: openQuestionsCount },
    { path: '/users', icon: Users, label: 'Benutzerverwaltung', badge: 0 },
    { path: '/backup', icon: Database, label: 'Backup', badge: 0 },
    { path: '/import', icon: Upload, label: 'Daten-Import', badge: 0 },
    { path: '/system-status', icon: Activity, label: 'System-Status', badge: 0 },
    { path: '/profile', icon: UserCircle, label: 'Mein Profil', badge: 0 },
    { path: '/settings', icon: Settings, label: 'Einstellungen', badge: 0 }
  ];

  // Mail-Module Navigation - nur für UMitza
  const mailNavItems = [
    { path: '/mail/inbox', icon: Inbox, label: 'Mail Inbox', badge: mailInboxCount },
    { path: '/mail/quick-actions', icon: Zap, label: 'Quick-Actions', badge: 0 },
    { path: '/mail/ai-config', icon: Sparkles, label: 'KI-Konfiguration', badge: 0 },
    { path: '/mail/rules', icon: Lightbulb, label: 'Regelvorschläge', badge: 0 },
    { path: '/mail/owners', icon: UserCheck, label: 'Eigentümer', badge: 0 },
    { path: '/mail/config', icon: Mail, label: 'Mail Konfiguration', badge: 0 }
  ];

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: '#0E4C92' }}>
      {/* Sidebar Header - weißer Hintergrund */}
      <div className="p-4 bg-white">
        <button
          type="button"
          onClick={handleNewChat}
          className="w-full py-2.5 px-4 text-white font-medium rounded-lg flex items-center justify-center gap-2 transition-colors hover:opacity-90"
          style={{ backgroundColor: '#0E4C92' }}
        >
          <Plus className="h-4 w-4" />
          Neuer Chat
        </button>
      </div>

      {/* Conversations List */}
      <div className="flex-1 overflow-y-auto px-2 py-2">
        {isGuest ? (
          <div className="p-4 text-center text-blue-200 text-sm">
            <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>Als Gast wird kein Chat-Verlauf gespeichert.</p>
          </div>
        ) : conversations.length === 0 ? (
          <div className="p-4 text-center text-blue-200 text-sm">
            <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>Noch keine Unterhaltungen</p>
            <p className="text-xs mt-1 opacity-70">Chats werden 48h gespeichert</p>
          </div>
        ) : (
          <div className="space-y-1">
            {conversations.map((conv) => (
              <div
                key={`conv-${conv.id}`}
                onClick={() => handleSelectChat(conv)}
                className={`w-full text-left p-3 rounded-lg transition-colors group relative cursor-pointer ${
                  activeChatId === conv.id
                    ? 'bg-white/20 text-white'
                    : 'text-blue-100 hover:bg-white/10'
                }`}
              >
                <div className="flex items-start gap-2">
                  <MessageSquare className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0 pr-6">
                    <p className="text-sm font-medium truncate">{conv.title}</p>
                    {conv.preview && (
                      <p className="text-xs opacity-70 truncate mt-0.5">{conv.preview}</p>
                    )}
                    <p className="text-xs opacity-50 mt-1">{formatTimestamp(conv.updated_at || conv.created_at)}</p>
                  </div>
                </div>
                {/* Delete button */}
                <button
                  type="button"
                  onClick={(e) => handleDeleteChat(e, conv.id)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded opacity-0 group-hover:opacity-100 hover:bg-red-500/20 text-blue-200 hover:text-red-400 transition-all"
                  title="Chat löschen"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sidebar Footer - Navigation based on role */}
      {!isGuest && (
        <div className="p-4 border-t border-blue-800 space-y-1">
          {/* Mail Module Section - nur für UMitza */}
          {isUMitza && (
            <>
              <div className="text-xs text-blue-300/70 uppercase tracking-wider px-4 py-2 font-semibold">
                Mail Automation
              </div>
              {mailNavItems.map((item) => {
                const IconComponent = item.icon;
                return (
                  <button
                    type="button"
                    key={`mail-nav-${item.path}`}
                    onClick={() => handleNavigation(item.path)}
                    data-testid={`sidebar-${item.path.replace(/\//g, '-')}`}
                    className={`w-full py-2.5 px-4 text-left rounded-lg flex items-center gap-2 transition-colors ${
                      isActive(item.path)
                        ? 'bg-white/20 text-white'
                        : 'text-blue-100 hover:bg-white/10'
                    }`}
                  >
                    <IconComponent className="h-4 w-4" />
                    <span className="flex-1">{item.label}</span>
                    {item.badge > 0 && (
                      <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold bg-red-500 text-white rounded-full">
                        {item.badge > 99 ? '99+' : item.badge}
                      </span>
                    )}
                  </button>
                );
              })}
              <div className="border-t border-blue-700 my-2"></div>
            </>
          )}
          
          {/* Standard Navigation */}
          {(isAdmin ? adminNavItems : employeeNavItems).map((item) => {
            const IconComponent = item.icon;
            return (
              <button
                type="button"
                key={`nav-${item.path}`}
                onClick={() => handleNavigation(item.path)}
                className={`w-full py-2.5 px-4 text-left rounded-lg flex items-center gap-2 transition-colors ${
                  isActive(item.path)
                    ? 'bg-white/20 text-white'
                    : 'text-blue-100 hover:bg-white/10'
                }`}
              >
                <IconComponent className="h-4 w-4" />
                <span className="flex-1">{item.label}</span>
                {item.badge > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold bg-red-500 text-white rounded-full">
                    {item.badge > 99 ? '99+' : item.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
      
      {/* Version Badge am unteren Rand */}
      <div className="p-3 border-t border-blue-800 text-center">
        <VersionBadge className="text-blue-300/60" />
      </div>
    </div>
  );
};
